import { dist2, imax, imin, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import type { PenBoar, Pt, TrailBranch, TrailCrossing } from '../caza-types.ts';
import { enemiesNear, findEnemy, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { castSkill } from '../battle/skills.ts';
import { stoneStand, trailOrder, trailWashed } from '../battle/trail.ts';
import { branchSeen } from '../battle/caza-layout.ts';
import { useSkills } from './ghost.ts';
import { vertBot } from './vert-bot.ts';

/**
 * The lab's bots for the Long Hunt's chapter 1 (docs/sendas/barbaro.md; docs/SENDAS.md, «Calibración»), apart from
 * ai/senda-bot.ts (it asks this file first):
 *
 * - the broken corral (`pens`): who knows goes for the wolf dragging a boar first (the one nearest its wall), then the
 *   ones going for the herd, then what is by the herd; who doesn't fights whatever is nearest.
 * - the tracks (`tracks`, `brink`): who knows reads the clues as a player would, only within sight (three hexes): it
 *   rules out the branches that jump or drink, takes the one with the pit, and tries the faintest of the rest first,
 *   walking it until it shows what it is; washed out, it brings Terrón. On the ledge it cuts every howl (a stone on the
 *   howler when one lies in line, else at it). Who doesn't takes the most trodden branch first, then the next, and
 *   fights what is nearest.
 *
 * It only moves the hero and picks its target, as a player's commands would; nothing it does is outside the rules.
 */

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

const strike = (s: BattleState, h: Hero, t: Enemy): void => {
  useSkills(s, h, t);
  if (h.skillCd[2] === 0 && enemiesNear(s, t.x, t.y, 2500).length >= 3) castSkill(s, h, { t: 'skill', slot: 2, x: t.x, y: t.y, id: t.id });
  h.target = t.id;
  h.forced = true;
};

/* ---------- the corral ---------- */

const pensBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.pens;
  const w = s.senda?.pens;
  if (!r || !w) return;
  if (!knows) {
    const t = nearestTo(enemiesNear(s, h.x, h.y, 16000, (e) => targetable(s, e)), h.x, h.y);
    if (t) return strike(s, h, t);
    h.target = 0;
    return;
  }
  // The wolf dragging a boar nearest its wall (least time left), then those going for the herd.
  let best: Enemy | null = null;
  let left = 0x7fffffff;
  for (const p of w.pack) {
    if (p.boar < 0) continue;
    const e = findEnemy(s, p.id);
    if (!e || e.hp <= 0) continue;
    const t = p.until > 0 ? p.until - s.tick : 1000 + dist2(e.x, e.y, h.x, h.y) / 1_000_000;
    if (t < left) {
      left = t;
      best = e;
    }
  }
  if (best) return strike(s, h, best);
  // By the herd: what is near it, else stand before it.
  const c = r.homes[0] as Pt;
  const near = nearestTo(enemiesNear(s, c.x, c.y, 4500, (e) => targetable(s, e)), h.x, h.y);
  if (near) return strike(s, h, near);
  const t = nearestTo(enemiesNear(s, h.x, h.y, 2600, (e) => targetable(s, e)), h.x, h.y);
  if (t) return strike(s, h, t);
  h.target = 0;
  go(s, h, c.x, c.y + 1800);
};

/* ---------- the tracks ---------- */

/** What the bot that reads has seen at each crossing: the pack's branches ruled out, the Wolf's pit seen. */
interface Read {
  at: number;
  out: boolean[];
  pit: number;
  on: number;
}
const READ = new WeakMap<BattleState, Read>();

/** The branch the bot that doesn't read tries next: the most trodden not yet found wrong. */
const blindPick = (c: TrailCrossing, wrong: boolean[]): number => {
  let best = -1;
  for (let i = 0; i < c.branches.length; i++) {
    if (wrong[i]) continue;
    if (best < 0 || (c.branches[i] as TrailBranch).marks > (c.branches[best] as TrailBranch).marks) best = i;
  }
  return best < 0 ? 0 : best;
};

/** Walks a branch from where the hero is along it, a few points ahead. */
const walk = (s: BattleState, h: Hero, b: TrailBranch, prog: number): void => {
  const i = imin(b.pts.length - 1, imax(0, prog) + 3);
  const p = b.pts[i] as Pt;
  h.target = 0;
  go(s, h, p.x, p.y, 200);
};

/** On the ledge, the howl: a loose stone with the howler in its line, else at it. True if busy with it. */
const cutHowl = (s: BattleState, h: Hero): boolean => {
  const st = s.senda?.howler;
  const r = s.rules?.senda?.trail;
  const w = s.senda?.trail;
  if (!st || !r || !w || (st.phase !== 1 && st.phase !== 2)) return false;
  const e = findEnemy(s, st.id);
  if (!e || e.hp <= 0) return false;
  // A stone it can reach in the time left, the howler in its roll.
  if (r.stones && r.stone && w.job <= 0) {
    for (let k = 0; k < r.stones.length; k++) {
      const stn = w.stones[k];
      const p = r.stones[k] as Pt;
      if (!stn || stn.mode !== 0) continue;
      const q = stoneStand(p);
      if (!withinRange(q.x, q.y, h.x, h.y, 2600)) continue;
      if (Math.abs(e.y - p.y) > r.stone.width - 150 || e.x < p.x || e.x > p.x + r.stone.roll - 500) continue;
      trailOrder(s, h, k + 1);
      return true;
    }
  }
  if (w.job > 0) return true;
  strike(s, h, e);
  return true;
};

const trailBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.trail;
  const w = s.senda?.trail;
  if (!r || !w || w.done >= 0) return;
  const c = r.crossings[w.at];
  if (!c) return;
  if (knows && cutHowl(s, h)) return;
  // Pushing a stone: keep at it.
  if (w.job > 0) return;
  // What comes close, first.
  const t = nearestTo(enemiesNear(s, h.x, h.y, knows ? 1700 : 2000, (e) => targetable(s, e)), h.x, h.y);
  if (t) return strike(s, h, t);
  // Terrón at work: wait by the crossing.
  if (w.sniff >= 0) {
    h.target = 0;
    return;
  }
  if (!knows) {
    const b = blindPick(c, w.wrong);
    return walk(s, h, c.branches[b] as TrailBranch, w.prog[b] ?? -1);
  }
  let rd = READ.get(s);
  if (!rd || rd.at !== w.at) {
    rd = { at: w.at, out: c.branches.map(() => false), pit: -1, on: -1 };
    READ.set(s, rd);
  }
  const washed = trailWashed(s, r, w.at);
  // Shown by Terrón, or washed (Terrón to the crossing).
  if (w.shown.includes(w.at)) return walk(s, h, c.branches[c.right] as TrailBranch, w.prog[c.right] ?? -1);
  if (washed) {
    if (w.job !== 0) trailOrder(s, h, 0);
    return;
  }
  // What can be read from here.
  c.branches.forEach((b, i) => {
    const seen = branchSeen(b, h, r.see);
    if (seen.pack || w.wrong[i]) rd.out[i] = true;
    if (seen.pit) rd.pit = i;
  });
  let pick = rd.pit;
  if (pick < 0) {
    const left = c.branches.map((_, i) => i).filter((i) => !rd.out[i]);
    if (left.length === 1) pick = left[0] as number;
    else if (rd.on >= 0 && !rd.out[rd.on]) pick = rd.on;
    else {
      // The faintest of those left (the Wolf walks alone).
      pick = left.reduce((a, b) => ((c.branches[b] as TrailBranch).marks < (c.branches[a] as TrailBranch).marks ? b : a), left[0] ?? 0);
    }
  }
  rd.on = pick;
  walk(s, h, c.branches[pick] as TrailBranch, w.prog[pick] ?? -1);
};

/** The Long Hunt's act this bot plays (false: not one of its). */
export const cazaBot = (s: BattleState, h: Hero, knows: boolean): boolean => {
  const r = s.rules?.senda;
  if (r?.pens) {
    pensBot(s, h, knows);
    return true;
  }
  if (r?.trail) {
    trailBot(s, h, knows);
    return true;
  }
  // Chapter 2, «Las Vértebras» (ai/vert-bot.ts).
  return vertBot(s, h, knows);
};

/** (For tests: the herd's boars not lost.) */
export const herdLeft = (bs: PenBoar[]): number => bs.filter((b) => !b.lost).length;
